import React, { useRef, useEffect, useState } from 'react';
import { Search, MapPin, Loader } from 'lucide-react';
import { useMaps } from '../../contexts/MapsContext';

interface LocationSearchProps {
  onLocationSelect: (location: google.maps.LatLngLiteral) => void;
}

export default function LocationSearch({ onLocationSelect }: LocationSearchProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchValue, setSearchValue] = useState('');
  const { isLoaded } = useMaps();

  const validateTexasZipCode = (zip: string) => {
    // Texas ZIP codes range from 75001 to 79999 and 88510 to 88589
    const zipNum = parseInt(zip);
    return (zipNum >= 75001 && zipNum <= 79999) || (zipNum >= 88510 && zipNum <= 88589);
  };

  const handleGeolocation = () => {
    if ('geolocation' in navigator) {
      setIsLoading(true);
      setError(null);
      navigator.geolocation.getCurrentPosition(
        (position) => {
          onLocationSelect({
            lat: position.coords.latitude,
            lng: position.coords.longitude
          });
          setIsLoading(false);
        },
        (error) => {
          setError('Could not get your location. Please try searching instead.');
          setIsLoading(false);
        }
      );
    }
  };

  useEffect(() => {
    if (!isLoaded || !inputRef.current) return;

    // Create bounds for Texas
    const texasBounds = new google.maps.LatLngBounds(
      new google.maps.LatLng(25.8371, -106.6452),  // SW corner
      new google.maps.LatLng(36.5007, -93.5083)    // NE corner
    );

    const autocomplete = new google.maps.places.Autocomplete(inputRef.current, {
      componentRestrictions: { country: 'us' },
      fields: ['geometry', 'formatted_address', 'address_components'],
      types: ['address', 'geocode'],
      bounds: texasBounds,
      strictBounds: true
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      
      // Check if location is in Texas
      const isInTexas = place.address_components?.some(
        component => component.short_name === 'TX' && component.types.includes('administrative_area_level_1')
      );

      if (place.geometry?.location && isInTexas) {
        onLocationSelect({
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng()
        });
      } else {
        setError('Please enter a location within Texas');
      }
    });
  }, [isLoaded, onLocationSelect]);

  return (
    <div className="w-full max-w-xl mx-auto mb-8" role="search">
      <div className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="relative flex-grow">
            <div className="relative">
              <input
                ref={inputRef}
                aria-label="Search for BTM locations"
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                type="text"
                placeholder="Enter city, address, or ZIP code in Texas"
                className="w-full px-4 py-3 pl-12 bg-gray-700 border border-gray-600 rounded-lg text-white placeholder-gray-400 focus:border-purple-500 focus:ring-1 focus:ring-purple-500"
                disabled={isLoading || !isLoaded}
              />
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            </div>
            {error && (
              <p className="absolute -bottom-6 left-0 text-sm text-red-400">{error}</p>
            )}
          </div>
          <button
            onClick={handleGeolocation}
            disabled={isLoading || !isLoaded}
            className="flex items-center justify-center px-6 py-3 bg-purple-600 hover:bg-purple-700 disabled:bg-purple-800 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
          >
            {isLoading ? (
              <Loader className="w-5 h-5 animate-spin" />
            ) : (
              <>
                <MapPin className="w-5 h-5 mr-2" />
                <span>Use My Location</span>
              </>
            )}
          </button>
        </div>
        <p className="mt-4 text-gray-400 text-sm">
          Enter your location to find the nearest Bitcoin ATMs. All our BTMs are available 24/7.
        </p>
      </div>
    </div>
  );
}